import { useForm, Controller } from 'react-hook-form'
import { GoogleIcon } from '@/components/icons'
import SvgSpinner from '@/components/icons/spinner'
import React, { useCallback, useMemo, useState } from 'react'
import { notify } from '@/utils/index'
import {
  login,
  loginWithGoogle,
  setAuthenticated,
  setUserPending,
} from '@/redux/slices/auth'
import { useDispatch } from 'react-redux'
import { useRouter, useSearchParams } from 'next/navigation'
import SvgSpinnerBlue from '@/components/icons/spinner-blue'
import useCreateBookedCourse from '@/hooks/courses/createBookedCourse'
import { useSelector } from 'react-redux'
import jobselector from '@/redux/selectors/jobs'
import {
  sendEmailToSupport,
  sentEmailBookedSlotStudent,
  sentEmailBookedSlotTutor,
} from '@/services/novu-notifications'
import moment from 'moment'
import { firebaseBookLessonByStudent } from '@/firebase/students'
import { toast } from 'react-toastify'
import { updateJobDetail } from '@/redux/slices/jobs'
import { StudentBookedTutorToSupport } from '@/utils/email-templates'
import { UserRole } from '../../../types/firebase/user'
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit'
import { IAuthState } from '@/types/redux'
import { IGenericError } from '@/types/shared'
import { CommonPasswordInput } from '../common'
import * as yup from 'yup'
import {
  requiredFieldErrorMessage,
  tooShortErrorMessage,
} from '@/constants/yup-error-messages'
import { yupResolver } from '@hookform/resolvers/yup'
import { validateEmail } from '@/utils/validation'
import { subscriptionsHref } from '@/utils/mixpanel/events/paywallSource'

const signUpSchema = yup
  .object({
    email: yup.string().defined(),
    password: yup.string().defined(),
  })
  .shape(
    {
      email: yup.string().email().required(requiredFieldErrorMessage),
      password: yup
        .string()
        .trim()
        .required(requiredFieldErrorMessage)
        .min(
          8,
          `${tooShortErrorMessage}. Password should contain at least 8 symbols`,
        ),
    },
    [],
  )
  .required()

interface ISignUpFormValues {
  email: string
  password: string
}

const initialFormValues: ISignUpFormValues = { email: '', password: '' }

function SignInForm({
  registerSignUp = false,
  stopCourseRedirect = false,
  onClose,
  messageTutor = false,
  setSignIn,
  setIsMessageTutor,
  bookedTutor,
  setIsBookedTutor,
  handleEmbeddedPageRedirectToSubscription,
}) {
  const [loading, setLoading] = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)

  const dispatch = useDispatch()
  const thunkDispatch = dispatch as ThunkDispatch<
    IAuthState,
    unknown,
    AnyAction
  >

  const router = useRouter()
  const { jobDetail } = useSelector(jobselector)
  const params = useSearchParams()
  const courseId = params?.get('courseId')
  const paperId = params?.get('paperId')
  const topicId = params?.get('topicId')
  const lessonId = params?.get('lessonId')
  const subscriptionPlanId = params?.get('subscriptionPlanId')

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<ISignUpFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: initialFormValues,
    resolver: yupResolver(signUpSchema),
  })

  const callMyHook = async (values) => {
    const { user, router, dispatch, courseId, topicId, lessonId } = values
    console.log(values, 'values')
    const value = await useCreateBookedCourse(
      user,
      router,
      dispatch,
      courseId,
      topicId,
      lessonId,
    )
    // Do something with value.
  }

  const bookTutorByStudent = useCallback(
    async (lessonData, user) => {
      try {
        const response = await firebaseBookLessonByStudent(lessonData)
        if (response.success) {
          // Send the notification to the tutor
          await sentEmailBookedSlotTutor({
            name: lessonData?.tutorsName,
            studentName: lessonData?.studentName,
            date: moment(lessonData?.date).format('MMM Do, YYYY'),
            topic: '',
            subjects: lessonData?.subjects,
            timeSlots: [lessonData?.slot],
            recipientEmail: lessonData?.tutorsEmail,
            userId: lessonData?.tutorsId,
            link: `${window.location.origin}/my-jobs/${response.jobId}`,
          })

          // Send the notification to the student
          await sentEmailBookedSlotStudent({
            name: lessonData?.studentName,
            tutorName: lessonData?.tutorsName,
            date: moment(lessonData?.date).format('MMM Do, YYYY'),
            timeSlots: [lessonData?.slot],
            topic: '',
            subjects: lessonData?.subjects,
            recipientEmail: user?.email,
            userId: user?.uid,
          })

          // send the notification
          await sendEmailToSupport({
            emailSubject: 'Student Booked Tutor',
            emailBody: StudentBookedTutorToSupport({
              tutorName: lessonData?.tutorsName,
              studentName: lessonData?.studentName,
              timeSlots: [lessonData?.slot],
              date: moment(lessonData?.date).format('MMM Do, YYYY'),
            }),
          })

          toast.success(
            'The tutor has received your request and will get back to you shortly.',
          )
          router.push('/student/one-to-one')
        }
      } catch (error) {
        console.log(error)
        toast.error('Something went wrong!')
      }
    },
    [router],
  )

  const onSubmit = useCallback(
    async (values: ISignUpFormValues) => {
      const { email, password } = values

      // check empty email/password
      if (!email || !password) {
        notify('error', 'Please fill all fields.')
        setLoading(false)
        return null
      }

      // validate email
      if (!validateEmail(email)) {
        setLoading(false)
        notify('error', 'Please enter a valid email.')
        return
      }

      // check password length
      if (!(password.length >= 8)) {
        setLoading(false)
        notify('error', 'Password length must be greater than 8 characters.')
        return
      }

      try {
        setLoading(true)

        const { user, error } = await thunkDispatch(login(values)).unwrap()

        if (!stopCourseRedirect && user && !error) {
          localStorage.setItem('user', JSON.stringify(user))

          if (user.role === UserRole.Admin) {
            router.replace('/admin')
          } else if (user.role.toLowerCase() === UserRole.Student) {
            // handle redirect to payment page if came from embedded exam-pack-subscription page
            if (!!subscriptionPlanId) {
              await handleEmbeddedPageRedirectToSubscription(
                subscriptionPlanId,
                user.uid,
                user.role,
              )
              dispatch(setAuthenticated(true))
              dispatch(setUserPending(false))

              return
            }

            if (courseId) {
              await callMyHook({
                user,
                router,
                dispatch,
                courseId,
                topicId,
                lessonId,
              })
              dispatch(setAuthenticated(true))
              dispatch(setUserPending(false))
            } else if (paperId) {
              const isCompleted = user.welcomeModal
              dispatch(setAuthenticated(true))
              dispatch(setUserPending(false))
              let pathPaper = isCompleted
                ? `/student/exam-paper/view?id=${paperId}`
                : `/student/dashboard?paperId=${paperId}`
              router.replace(pathPaper)
            } else {
              dispatch(setAuthenticated(true))
              dispatch(setUserPending(false))
              // router.replace('/student')
            }

            if (messageTutor && !bookedTutor) {
              setSignIn(false)
              setIsMessageTutor(true)
            } else if (jobDetail) {
              bookTutorByStudent(
                {
                  studentsId: user.uid,
                  studentName: user.name,
                  ...jobDetail,
                },
                user,
              )
              setTimeout(() => {
                dispatch(updateJobDetail(null))
              }, 1000)
            } else if (bookedTutor) {
              router.push(
                `student/book-tutor/tutoring-type?id=${messageTutor?.uid}`,
              )
            } else if (bookedTutor) {
              // router.push(
              //   `student/book-tutor/tutoring-type?id=${messageTutor?.uid}`,
              // )
              setIsBookedTutor(true)
            } else {
              // TODO: Need to be modified. Fixed for GA4 re-initialisation.
              !paperId &&
                !courseId &&
                (window.location.href = window.location.origin + '/student')
            }
            const isFromSub = localStorage.getItem('isFromSubscribe')
            if (isFromSub) {
              router.replace('/subscriptions')
            }
          }
        } else if (user.role.toLowerCase() === 'tutor') {
          router.replace('tutors-dashboard')
        } else if (user.role.toLowerCase() === 'parent') {
          router.replace('parent/dashboard')
          console.log('parent', user)
        } else if (user.role.toLowerCase() === 'guardian') {
          router.replace('parent/dashboard')
          console.log('guardian', user)
        } else if (user.role.toLowerCase() === 'schooladmin') {
          router.replace('school/members')
        } else {
          router.replace('/')
        }
      } catch (error: unknown) {
        console.error(
          'error onSubmit :>> ',
          (error as IGenericError)?.message || error,
          error,
        )

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        setLoading(false)
        onClose && onClose(false)
      }
    },
    [
      bookTutorByStudent,
      bookedTutor,
      courseId,
      dispatch,
      handleEmbeddedPageRedirectToSubscription,
      jobDetail,
      lessonId,
      messageTutor,
      onClose,
      paperId,
      router,
      setIsBookedTutor,
      setIsMessageTutor,
      setSignIn,
      stopCourseRedirect,
      subscriptionPlanId,
      thunkDispatch,
      topicId,
    ],
  )

  const signInWithGoogle = async () => {
    try {
      setLoadingGoogle(true)

      const { user, error } = await thunkDispatch(loginWithGoogle()).unwrap()

      if (!stopCourseRedirect && user && !error) {
        localStorage.setItem('user', JSON.stringify(user))

        if (user.role.toLowerCase() === 'student') {
          // handle redirect to payment page if came from embedded exam-pack-subscription page
          if (!!subscriptionPlanId) {
            await handleEmbeddedPageRedirectToSubscription(
              subscriptionPlanId,
              user?.uid,
              user?.role,
            )

            return
          }

          if (courseId) {
            await callMyHook({
              user,
              router,
              dispatch,
              courseId,
              topicId,
              lessonId,
            })
            dispatch(setAuthenticated(true))
            dispatch(setUserPending(false))
          } else if (paperId) {
            dispatch(setAuthenticated(true))
            dispatch(setUserPending(false))
            let pathPaper = `/student/dashboard?paperId=${paperId}`
            router.replace(pathPaper)
          } else {
            dispatch(setAuthenticated(true))
            dispatch(setUserPending(false))
            // router.replace('/student')
          }
          if (messageTutor && !bookedTutor) {
            setSignIn(false)
            setIsMessageTutor(true)
          } else if (jobDetail) {
            bookTutorByStudent(
              {
                studentsId: user?.uid,
                studentName: user?.name,
                ...jobDetail,
              },
              user,
            )
          } else if (bookedTutor) {
            router.push(
              `student/book-tutor/tutoring-type?id=${messageTutor?.uid}`,
            )
          } else if (bookedTutor) {
            // router.push(
            //   `student/book-tutor/tutoring-type?id=${messageTutor?.uid}`,
            // )
            setIsBookedTutor(true)
          } else {
            // TODO: Need to be modified. Fixed for GA4 re-initialisation.
            // router.replace('/student')
            window.location.href = window.location.origin + '/student'
          }

          const isFromSub = localStorage.getItem('isFromSubscribe')
          if (isFromSub) {
            router.replace(subscriptionsHref('login_redirect'))
          }
        } else if (user?.role.toLowerCase() === 'parent') {
          router.replace('parent/dashboard')
          console.log('parent', user)
        } else if (user?.role.toLowerCase() === 'school') {
          router.replace('parent/dashboard')
          console.log('guardian', user)
        } else {
          router.replace('/')
        }
      }
    } catch (error: unknown) {
      console.error(
        'error signInWithGoogle :>> ',
        (error as IGenericError)?.message || error,
        error,
      )

      if (error && typeof error === 'object' && 'message' in error) {
        notify('error', error.message)
      }
    } finally {
      setLoadingGoogle(false)
    }
  }

  const isDisabledBtn: boolean = useMemo(() => {
    return loading || loadingGoogle
  }, [loading, loadingGoogle])

  return (
    <div className="flex flex-col justify-center mt-5 sm:mt-0 item-center">
      <div className="w-full mt-2 sm:mx-auto md:max-w-md">
        <div className="px-4 py-2 bg-white sm:rounded-lg sm:px-[40px] relative">
          <form
            className="flex flex-col gap-5"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Email */}
            <div className="relative w-full flex flex-col gap-1">
              <label
                htmlFor="email"
                className="pl-1 font-medium text-v8-mut"
              >
                Email Address
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <input
                    type="text"
                    id="email"
                    className="block w-full rounded-md h-10 text-v8-mut font-normal text-md leading-6 border-[#e6e6e6] placeholder-gray-400 shadow-sm focus:border-v8-blue focus:ring-v8-blue"
                    placeholder="Email"
                    {...field}
                    ref={field.ref}
                    autoFocus
                  />
                )}
              />

              {errors.email && (
                <p className="absolute bottom-[-16px] sm:bottom-[-20px] pl-1 text-[10px] sm:text-xs text-red-600 text-start mt-2">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password */}
            <div className="relative w-full flex flex-col gap-1">
              <label
                htmlFor="password"
                className="pl-1 font-medium text-v8-mut"
              >
                Password
              </label>

              <Controller
                name="password"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <CommonPasswordInput
                    id="password"
                    placeholder="Enter Password"
                    required
                    {...field}
                    containerStyles="p-1 w-full rounded-md border border-[#e6e6e6] bg-white shadow-sm focus-within:border-v8-blue focus-within:ring-v8-blue focus-within:ring-1"
                    inputStyles="w-full text-md text-v8-mut border-none focus:border-none focus:outline-none focus:ring-0 p-1 bg-white"
                    eyeBtnStyles="focus:outline-none focus-visible:ring focus-visible:ring-v8-blue focus-visible:border-v8-blue rounded-sm"
                  />
                )}
              />

              {errors.password && (
                <p className="absolute bottom-[-16px] sm:bottom-[-20px] pl-1 text-[10px] sm:text-xs text-red-600 text-start mt-2">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* {inputAttributes.map((inputAttribute) => {
              let errorMessage: string | undefined = undefined

              if (errors[inputAttribute.name]) {
                errorMessage = errors[inputAttribute.name]?.message?.toString()
              }

              return (
                <InputMain
                  key={inputAttribute.name}
                  className="relative pb-0 border-none sm:gap-1"
                >
                  <InputMain.Label
                    htmlFor={inputAttribute.name}
                    className="text-sm font-medium text-v8-ink flex flex-start"
                    label={inputAttribute.title}
                    required={inputAttribute.required}
                  />
                  <Controller
                    name={inputAttribute.name}
                    control={control}
                    render={({ field }) => {
                      return (
                        <InputMain.Input
                          id={inputAttribute.name}
                          placeholder={inputAttribute.placeholder}
                          className="mb-8 sm:mb-4"
                          variant={
                            inputAttribute.variant
                              ? inputAttribute.variant
                              : 'default'
                          }
                          addon={
                            inputAttribute.variant ? inputAttribute.addon : ''
                          }
                          type={inputAttribute.type}
                          onInput={(e) => {
                            const { name, value } = e.target
                            setFormData({ ...formData, [name]: value })
                            // const removeWhiteSpace = ['username', 'email']
                            // if (removeWhiteSpace.includes(e.target.name)) {
                            //   e.target.value = e.target.value.replace(/\s/g, '')
                            // }
                          }}
                          {...field}
                          onChange={(e) => {
                            const { name, value } = e.target
                            setFormData({ ...formData, [name]: value })
                          }}
                          value={inputAttribute.value}
                        />
                      )
                    }}
                  />

                  {errorMessage && (
                    <span className="absolute text-xs text-red-600 -bottom-6 sm:-bottom-2 left-2">
                      {errorMessage}
                    </span>
                  )}
                </InputMain>
              )
            })} */}
            <div className="flex items-center justify-end pb-4">
              {/* Remember Me  */}
              {/* <div className="flex items-center">
                <input
                  id="authPersistence"
                  name="authPersistence"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      authPersistence: !formData.authPersistence,
                    })
                  }
                  type="checkbox"
                  className="h-4 w-4 rounded border-v8-line text-v8-blue focus:ring-v8-blue"
                />
                <label
                  htmlFor="authPersistence"
                  className="ml-2 block text-sm font-medium text-v8-ink"
                >
                  Remember me
                </label>
              </div> */}

              <div
                onClick={() => router.push(`/reset-password`)}
                className="cursor-pointer text-sm font-semibold leading-6 text-v8-blue"
              >
                Forgot password?
              </div>
            </div>
            {/* Submit Btn */}
            <button
              disabled={isDisabledBtn}
              type="submit"
              className="w-full py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm disabled:bg-v8-mut bg-v8-blue focus:outline-none focus:ring-offset-0 focus:ring-0 hover:bg-v8-blue transition duration-300"
            >
              {loading && <SvgSpinner />}
              Sign In
            </button>
          </form>

          <button
            disabled={loading || loadingGoogle}
            onClick={() => signInWithGoogle()}
            className="w-full mt-2 py-2 text-sm font-medium text-v8-ink border border-solid border-[#D5D9EB] rounded-md shadow-sm  focus:outline-none focus:ring-offset-0 focus:ring-0 flex justify-center gap-3 hover:bg-v8-blue/5 transition duration-300"
          >
            <span>{loadingGoogle && <SvgSpinnerBlue />}</span>
            <span>
              <GoogleIcon />
            </span>
            Sign in with Google
          </button>

          {registerSignUp && (
            <div className="flex items-center justify-center pt-4">
              <div className="ml-2 block text-sm font-medium text-v8-ink">
                {`Don't have an account?`}
              </div>

              <div
                onClick={() => {
                  let path = '/register'

                  // handle case when opened as exam-pack embedded page
                  if (!!subscriptionPlanId) {
                    path += `?subscriptionPlanId=${subscriptionPlanId}`

                    router.push(path)
                    return
                  }

                  if (courseId) {
                    path += `?courseId=${courseId}`
                    if (topicId) {
                      path += `&topicId=${topicId}`
                      if (lessonId) {
                        path += `&lessonId=${lessonId}`
                      }
                    }
                  }
                  if (paperId) {
                    path += `?paperId=${paperId}`
                  }
                  router.push(path)
                }}
                className="cursor-pointer text-sm font-semibold leading-6 text-v8-blue pl-3"
              >
                Sign Up
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SignInForm
